import React, { PureComponent } from 'react';
import Cookies from 'js-cookie';
import PropTypes from 'prop-types';
import { DashboardModalActions } from '@carvana/purchase-helpers';
import { Loading } from '@carvana/purchase-styles';
import OrderSummary from 'Components/order-summary';
import { EventTrackingActions } from '@carvana/purchase-utils';
import { Error } from '@carvana/purchase-styles';
import NavigationRail from './navigation-rail';
import { Wrapper, MainSection, MainSectionWrapper } from './dashboard.styles';
import { bucketUserInExperiment } from '../../services/index';
import DashboardLanding from './landing';
import MobileWebToMobileAppModal from './mobile-web-to-mobile-app-modal';
import { modalContentStyles } from './mobile-web-to-mobile-app-modal/mobile-web-to-mobile-app-modal.styles';
import WidgetBar from './widget-bar';

const { OpenModal } = DashboardModalActions;
const isCarvanaMobileAppUserAgent = () =>
  /CarvanaNativeWebView/gi.test(navigator.userAgent);
const hideHeader = Cookies.get('hide-header') === 'true' ? true : false;
const shouldHideHeader =
  /CarvanaNativeWebView/gi.test(navigator?.userAgent) && hideHeader;

class Dashboard extends PureComponent {
  static propTypes = {
    isMobile: PropTypes.bool.isRequired,
    isTablet: PropTypes.bool.isRequired,
    showSummary: PropTypes.bool.isRequired,
    location: PropTypes.shape({}).isRequired,
    vehicleLock: PropTypes.shape({}).isRequired,
    routeTo: PropTypes.func.isRequired,
    CancelCurrentPurchaseThenNewStartPurchaseWithVehicleId:
      PropTypes.func.isRequired,
    StartPurchaseWithVehicleId: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    hasSavedVehicleId: PropTypes.bool,
    hasBrowserVehicleId: PropTypes.bool,
    doesBrowserVehicleIdMatchSavedVehicleId: PropTypes.bool,
    isVehicleAvailable: PropTypes.bool,
    orderState: PropTypes.string,
    browserVehicleId: PropTypes.number,
    savedVehicleId: PropTypes.number,
    isError: PropTypes.bool,
    orderId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    children: PropTypes.node,
    dashboard: PropTypes.shape({
      isCancelInProcess: PropTypes.bool,
      vehicle: PropTypes.shape({})
    }),
    isLockedTimerPaused: PropTypes.bool,
    dashboardExperiments: PropTypes.shape({}).isRequired,
    userId: PropTypes.string.isRequired,
    needsToScheduleTimeSlot: PropTypes.bool,
    isWIPVehicle: PropTypes.bool,
    isReservedVehicle: PropTypes.bool,
    isPurchasable: PropTypes.bool,
    navigationConfiguration: PropTypes.shape({
      removePadding: PropTypes.bool
    }),
    eligibleToPurchase: PropTypes.bool,
    vehiclePrice: PropTypes.number
  };

  static defaultProps = {
    isLoading: false,
    children: null,
    orderId: null,
    hasSavedVehicleId: false,
    hasBrowserVehicleId: false,
    doesBrowserVehicleIdMatchSavedVehicleId: false,
    isVehicleAvailable: false,
    orderState: null,
    browserVehicleId: null,
    savedVehicleId: null,
    isError: false,
    dashboard: {},
    isLockedTimerPaused: false,
    needsToScheduleTimeSlot: null,
    isWIPVehicle: false,
    isReservedVehicle: false,
    isPurchasable: false,
    eligibleToPurchase: true,
    vehiclePrice: null,
    navigationConfiguration: {
      removePadding: false
    },
    isMobile: false,
    isTablet: false
  };

  constructor(props) {
    super(props);
    this.state = {
      isInPurchaseHeaderBucket: null
    };
  }

  async componentDidMount() {
    const {
      location: { pathname },
      hasSavedVehicleId,
      hasBrowserVehicleId,
      doesBrowserVehicleIdMatchSavedVehicleId,
      isVehicleAvailable,
      routeTo,
      orderState,
      browserVehicleId,
      savedVehicleId,
      isWIPVehicle,
      isReservedVehicle,
      isPurchasable,
      GetDashboard,
      CancelCurrentPurchaseThenNewStartPurchaseWithVehicleId,
      StartPurchaseWithVehicleId,
      isError,
      vehicleLock: { isAvailable } = {},
      dashboard: { isCancelInProcess } = {},
      isLockedTimerPaused,
      userId,
      vehiclePrice,
      isMobile,
      isTablet,
      experiments
    } = this.props;

    const assignedPurchaseHeaderBucket = await experiments.GetAssignedBucket({
      identifier: userId,
      experimentName: 'pp-purchase-header-dec-2023',
      environment: process.env.NODE_ENV
    });

    if (isCancelInProcess) return null;
    if (assignedPurchaseHeaderBucket === null) return null;

    const isInPurchaseHeaderBucket =
      assignedPurchaseHeaderBucket?.toLowerCase() === 'purchase-header';

    this.setState({ isInPurchaseHeaderBucket, error: null });

    const closeModalCallback = () => {
      EventTrackingActions.TrackEvent({
        name: 'Closed native app offering modal'
      });
    };

    const mobileAppModalCookie = Cookies.get('mobileAppModalLoaded');

    let experiment = {};

    if (orderState === 'inprogress') {
      experiment = await bucketUserInExperiment(
        userId,
        'pp-mobweb-to-app-cta-jul-2023'
      );
    }

    if (
      experiment?.content?.bucketKey === 'mobweb-to-app-cta' &&
      !isCarvanaMobileAppUserAgent() &&
      orderState === 'inprogress' &&
      !mobileAppModalCookie &&
      (isMobile || isTablet)
    ) {
      Cookies.set('mobileAppModalLoaded', true);
      OpenModal({
        modalContentStyles,
        headerText: 'DOWNLOAD OUR APP',
        ModalChildren: MobileWebToMobileAppModal,
        isOneTimeLoad: true,
        closeModalCallback
      });
    }

    if (
      hasBrowserVehicleId &&
      hasSavedVehicleId &&
      !doesBrowserVehicleIdMatchSavedVehicleId &&
      !isAvailable &&
      !isCancelInProcess
    ) {
      await CancelCurrentPurchaseThenNewStartPurchaseWithVehicleId(
        browserVehicleId
      )
        .then(() => GetDashboard())
        .then(() => routeTo('/dashboard-reload', 'replace'))
        .catch(err => {
          if (err.response?.status === 409) {
            routeTo('/vehicle/unavailable', 'replace');
          } else {
            this.setState({ isInPurchaseHeaderBucket, error: err });
          }
        });
      return;
    }

    if (vehiclePrice != null && vehiclePrice < 6000) {
      return routeTo('/error-price', 'replace');
    }

    if (
      isReservedVehicle &&
      isPurchasable &&
      hasBrowserVehicleId &&
      hasSavedVehicleId
    ) {
      return StartPurchaseWithVehicleId(savedVehicleId)
        .then(() => GetDashboard())
        .then(() => routeTo('/dashboard-reload', 'replace'))
        .catch(err => {
          if (err.response?.status === 409) {
            routeTo('/vehicle/unavailable', 'replace');
          } else {
            this.setState({ isInPurchaseHeaderBucket, error: err });
          }
        });
    }

    if (hasBrowserVehicleId && !hasSavedVehicleId) {
      return StartPurchaseWithVehicleId(browserVehicleId)
        .then(() => GetDashboard())
        .then(() => routeTo('/dashboard-reload', 'replace'))
        .catch(err => {
          if (err.response?.status === 409) {
            routeTo('/vehicle/unavailable', 'replace');
          } else {
            this.setState({ isInPurchaseHeaderBucket, error: err });
          }
        });
    }

    const isCurrentLocationVehicle = pathname?.includes('/vehicle/');

    if (!isCurrentLocationVehicle) {
      if (!hasBrowserVehicleId && !hasSavedVehicleId) {
        return routeTo('/vehicle/missing', 'replace');
      }

      if (
        hasBrowserVehicleId &&
        hasSavedVehicleId &&
        !doesBrowserVehicleIdMatchSavedVehicleId &&
        !isLockedTimerPaused
      ) {
        return routeTo('/vehicle/confirmation', 'replace');
      }

      if (isReservedVehicle && hasSavedVehicleId) {
        window.location.href = `/vehicle/${savedVehicleId}`;
        return null;
      }

      if (!isVehicleAvailable) {
        return routeTo('/vehicle/unavailable', 'replace');
      }

      if (isWIPVehicle && hasSavedVehicleId) {
        window.location.href = `/vehicle/${savedVehicleId}`;
        return null;
      }
    }

    return null;
  }

  render() {
    const {
      children,
      isLoading,
      isMobile,
      showSummary,
      isReservedVehicle,
      isWIPVehicle,
      navigationConfiguration,
      navigationConfiguration: { removePadding } = {},
      orderState,
      vehicleLock,
      vehicleLock: { isAvailable } = {},
      dashboard: { isCancelInProcess, vehicle } = {}
    } = this.props;

    const { isInPurchaseHeaderBucket, error } = this.state;

    const isError = (error !== undefined && error !== null);
    if (isError) {
       throw error;
    }

    return (
      <Wrapper isLoading={isLoading || isCancelInProcess} hideMobilePadding>
        {(isLoading || isCancelInProcess) && <Loading />}

        {!isLoading &&
          !isCancelInProcess &&
          !isReservedVehicle &&
          !isWIPVehicle && (
            <>
              {!(isMobile && showSummary) && (
                <NavigationRail
                  navigationConfiguration={navigationConfiguration}
                  isMobile={isMobile}
                  isInPurchaseHeaderBucket={isInPurchaseHeaderBucket}
                />
              )}

              <MainSection>
                {!shouldHideHeader && !isInPurchaseHeaderBucket && (
                  <WidgetBar
                    navigationConfiguration={navigationConfiguration}
                    isMobile={isMobile}
                    isAvailable={isAvailable}
                    orderState={orderState}
                    vehicleLock={vehicleLock}
                    vehicle={vehicle}
                  />
                )}
                {showSummary && (
                  <MainSectionWrapper
                    isInPurchaseHeaderBucket={isInPurchaseHeaderBucket}
                  >
                    <OrderSummary />
                  </MainSectionWrapper>
                )}
                <MainSectionWrapper
                  hide={showSummary}
                  removePadding={removePadding}
                  isInPurchaseHeaderBucket={isInPurchaseHeaderBucket}
                >
                  {children || <DashboardLanding {...this.props} />}
                </MainSectionWrapper>
              </MainSection>
            </>
          )}
      </Wrapper>
    );
  }
}

export default Dashboard;
